import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { api, AdminClient } from '../../lib/api';
import { useAuthStore } from '../../store/auth';
import {
  ArrowRightIcon,
  MagnifyingGlassIcon,
  FunnelIcon,
  PlusIcon,
  XMarkIcon,
  PauseIcon,
  ArchiveBoxIcon,
  TrashIcon,
  EllipsisVerticalIcon,
} from '@heroicons/react/24/outline';

const phaseNames: Record<number, string> = {
  1: 'Business Profile', 2: 'Business Intel', 3: 'Brand Guide', 4: 'ICP',
  5: 'Assets', 6: 'Website', 7: 'Blog', 8: 'Social', 9: 'Messaging', 10: 'Activation',
};

function AddClientModal({ onClose, onCreated }: { onClose: () => void; onCreated: (c: AdminClient) => void }) {
  const [form, setForm] = useState({
    email: '', business_name: '', contact_name: '', business_type: 'chiropractor',
    phone: '', website_url: '', city: '', state_province: '', country: 'US',
  });
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSaving(true);
    try {
      const data = await api.createClient(form);
      onCreated(data.client);
      onClose();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to create client');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-5 border-b border-gray-100">
          <h2 className="text-lg font-bold text-charcoal-800">Add New Client</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          {error && (
            <div className="bg-red-50 text-red-700 px-4 py-2.5 rounded-lg text-sm">{error}</div>
          )}
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">Business Name *</label>
              <input type="text" required value={form.business_name}
                onChange={e => setForm({ ...form, business_name: e.target.value })}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-copper-500"
                placeholder="Acme Chiropractic" />
            </div>
            <div className="col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
              <input type="email" required value={form.email}
                onChange={e => setForm({ ...form, email: e.target.value })}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-copper-500"
                placeholder="owner@example.com" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Contact Name</label>
              <input type="text" value={form.contact_name}
                onChange={e => setForm({ ...form, contact_name: e.target.value })}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-copper-500"
                placeholder="Dr. Jane Smith" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
              <input type="text" value={form.phone}
                onChange={e => setForm({ ...form, phone: e.target.value })}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-copper-500"
                placeholder="(555) 123-4567" />
            </div>
            <div className="col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">Website URL</label>
              <input type="url" value={form.website_url}
                onChange={e => setForm({ ...form, website_url: e.target.value })}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-copper-500"
                placeholder="https://www.example.com" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Business Type</label>
              <select value={form.business_type}
                onChange={e => setForm({ ...form, business_type: e.target.value })}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-copper-500">
                <option value="chiropractor">Chiropractor</option>
                <option value="dentist">Dentist</option>
                <option value="physiotherapy">Physiotherapy</option>
                <option value="optometry">Optometry</option>
                <option value="massage_therapy">Massage Therapy</option>
                <option value="acupuncture">Acupuncture</option>
                <option value="veterinary">Veterinary</option>
                <option value="barbershop">Barbershop</option>
                <option value="salon">Salon</option>
                <option value="med_spa">Med Spa</option>
                <option value="fitness">Fitness</option>
                <option value="other">Other</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">City</label>
              <input type="text" value={form.city}
                onChange={e => setForm({ ...form, city: e.target.value })}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-copper-500"
                placeholder="Portland" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">State/Province</label>
              <input type="text" value={form.state_province}
                onChange={e => setForm({ ...form, state_province: e.target.value })}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-copper-500"
                placeholder="OR" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Country</label>
              <input type="text" value={form.country}
                onChange={e => setForm({ ...form, country: e.target.value })}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-copper-500"
                placeholder="US" />
            </div>
          </div>
          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={onClose}
              className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 transition-colors">
              Cancel
            </button>
            <button type="submit" disabled={saving}
              className="px-5 py-2 bg-copper-500 text-white rounded-lg text-sm font-medium hover:bg-copper-600 transition-colors disabled:opacity-50">
              {saving ? 'Creating...' : 'Add Client'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function ConfirmModal({ title, message, confirmLabel, confirmClass, onConfirm, onClose }: {
  title: string; message: string; confirmLabel: string; confirmClass: string;
  onConfirm: () => void; onClose: () => void;
}) {
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm">
        <div className="p-5">
          <h3 className="text-lg font-bold text-charcoal-800 mb-2">{title}</h3>
          <p className="text-sm text-gray-600">{message}</p>
        </div>
        <div className="flex justify-end gap-3 p-5 border-t border-gray-100">
          <button onClick={onClose}
            className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800">Cancel</button>
          <button onClick={onConfirm}
            className={`px-4 py-2 text-sm font-medium rounded-lg text-white ${confirmClass}`}>
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function AdminClientsPage() {
  const [clients, setClients] = useState<AdminClient[]>([]);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [actionMenu, setActionMenu] = useState<string | null>(null);
  const [confirmAction, setConfirmAction] = useState<{
    clientId: string; clientName: string; action: 'suspend' | 'archive' | 'delete' | 'reactivate';
  } | null>(null);
  const { impersonate } = useAuthStore();

  useEffect(() => {
    const load = async () => {
      try {
        const data = await api.getClients();
        setClients(data.clients);
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClick = () => setActionMenu(null);
    if (actionMenu) document.addEventListener('click', handleClick);
    return () => document.removeEventListener('click', handleClick);
  }, [actionMenu]);

  const handleAction = async () => {
    if (!confirmAction) return;
    try {
      if (confirmAction.action === 'reactivate') {
        await api.updateClient(confirmAction.clientId, { status: 'onboarding' });
        setClients(prev => prev.map(c =>
          c.id === confirmAction.clientId ? { ...c, status: 'onboarding' } : c
        ));
      } else if (confirmAction.action === 'delete') {
        await api.deleteClient(confirmAction.clientId, 'delete');
        setClients(prev => prev.filter(c => c.id !== confirmAction.clientId));
      } else {
        await api.deleteClient(confirmAction.clientId, confirmAction.action);
        setClients(prev => prev.map(c =>
          c.id === confirmAction.clientId ? { ...c, status: confirmAction.action === 'archive' ? 'archived' : 'suspended' } : c
        ));
      }
    } catch (e) {
      console.error(e);
    }
    setConfirmAction(null);
  };

  const filtered = clients.filter(c => {
    const matchesSearch = c.business_name.toLowerCase().includes(search.toLowerCase()) ||
      c.email.toLowerCase().includes(search.toLowerCase()) ||
      (c.contact_name || '').toLowerCase().includes(search.toLowerCase());
    const matchesStatus = statusFilter === 'all' || c.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const statuses = ['all', ...Array.from(new Set(clients.map(c => c.status)))];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-copper-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-charcoal-800">Clients</h1>
          <p className="text-sm text-gray-500 mt-1">{clients.length} total client{clients.length !== 1 ? 's' : ''}</p>
        </div>
        <button onClick={() => setShowAddModal(true)}
          className="flex items-center gap-2 px-4 py-2.5 bg-copper-500 text-white rounded-lg text-sm font-medium hover:bg-copper-600 transition-colors shadow-copper-sm">
          <PlusIcon className="h-4 w-4" />
          Add Client
        </button>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <input type="text" placeholder="Search by name, email, or contact..."
            value={search} onChange={e => setSearch(e.target.value)}
            className="w-full pl-9 pr-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-copper-500 focus:border-transparent" />
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <FunnelIcon className="h-4 w-4 text-gray-400" />
          {statuses.map((s) => (
            <button key={s} onClick={() => setStatusFilter(s)}
              className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                statusFilter === s ? 'bg-copper-500 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}>
              {s === 'all' ? 'All' : s.charAt(0).toUpperCase() + s.slice(1)}
            </button>
          ))}
        </div>
      </div>

      {/* Client Cards */}
      <div className="grid gap-4">
        {filtered.map((client) => (
          <div key={client.id} className={`bg-white rounded-xl border p-5 hover:shadow-md transition-shadow ${
            client.status === 'suspended' || client.status === 'archived'
              ? 'border-gray-200 opacity-70'
              : 'border-gray-200'
          }`}>
            <div className="flex items-start justify-between">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-3 mb-2">
                  <Link to={`/admin/clients/${client.id}`}
                    className="font-semibold text-charcoal-800 hover:text-copper-600 text-lg">
                    {client.business_name}
                  </Link>
                  <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${
                    client.status === 'onboarding' ? 'bg-blue-100 text-blue-700' :
                    client.status === 'active' ? 'bg-green-100 text-green-700' :
                    client.status === 'suspended' ? 'bg-amber-100 text-amber-700' :
                    client.status === 'archived' ? 'bg-gray-100 text-gray-500' :
                    'bg-gray-100 text-gray-600'
                  }`}>
                    {client.status}
                  </span>
                  {client.unread_messages > 0 && (
                    <span className="bg-copper-500 text-white text-xs font-bold px-2 py-0.5 rounded-full">
                      {client.unread_messages} unread
                    </span>
                  )}
                </div>
                <div className="flex flex-wrap gap-x-6 gap-y-1 text-sm text-gray-500">
                  <span>{client.email}</span>
                  {client.contact_name && <span>{client.contact_name}</span>}
                  <span>{client.city}{client.state_province ? `, ${client.state_province}` : ''}</span>
                  <span>{client.business_type}</span>
                </div>
                <div className="mt-3 flex items-center gap-2">
                  <div className="flex gap-0.5">
                    {Array.from({ length: 10 }, (_, i) => (
                      <div key={i}
                        className={`w-6 h-2 rounded-full ${
                          (client.completed_phases || []).includes(i + 1) ? 'bg-teal-500' :
                          i + 1 === client.current_phase ? 'bg-copper-400' : 'bg-gray-200'
                        }`}
                        title={phaseNames[i + 1]} />
                    ))}
                  </div>
                  <span className="text-xs text-gray-400 ml-1">
                    Phase {client.current_phase} &middot; {phaseNames[client.current_phase]}
                  </span>
                </div>
              </div>
              <div className="flex items-center gap-2 ml-4 flex-shrink-0">
                <Link to={`/admin/clients/${client.id}`}
                  className="px-3 py-1.5 text-xs font-medium text-teal-600 border border-teal-200 rounded-lg hover:bg-teal-50 transition-colors">
                  View Details
                </Link>
                {client.status !== 'suspended' && client.status !== 'archived' && (
                  <button onClick={() => impersonate(client.id)}
                    className="px-3 py-1.5 text-xs font-medium text-copper-600 border border-copper-200 rounded-lg hover:bg-copper-50 transition-colors flex items-center gap-1">
                    Login as <ArrowRightIcon className="h-3 w-3" />
                  </button>
                )}
                {/* Actions dropdown */}
                <div className="relative">
                  <button onClick={(e) => { e.stopPropagation(); setActionMenu(actionMenu === client.id ? null : client.id); }}
                    className="p-1.5 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100 transition-colors">
                    <EllipsisVerticalIcon className="h-5 w-5" />
                  </button>
                  {actionMenu === client.id && (
                    <div className="absolute right-0 top-full mt-1 w-44 bg-white rounded-xl shadow-lg border border-gray-200 py-1 z-20">
                      {(client.status === 'suspended' || client.status === 'archived') && (
                        <button onClick={() => {
                          setConfirmAction({ clientId: client.id, clientName: client.business_name, action: 'reactivate' });
                          setActionMenu(null);
                        }}
                          className="w-full text-left px-4 py-2 text-sm text-green-600 hover:bg-green-50 flex items-center gap-2">
                          <ArrowRightIcon className="h-4 w-4" /> Reactivate
                        </button>
                      )}
                      {client.status !== 'suspended' && (
                        <button onClick={() => {
                          setConfirmAction({ clientId: client.id, clientName: client.business_name, action: 'suspend' });
                          setActionMenu(null);
                        }}
                          className="w-full text-left px-4 py-2 text-sm text-amber-600 hover:bg-amber-50 flex items-center gap-2">
                          <PauseIcon className="h-4 w-4" /> Pause
                        </button>
                      )}
                      {client.status !== 'archived' && (
                        <button onClick={() => {
                          setConfirmAction({ clientId: client.id, clientName: client.business_name, action: 'archive' });
                          setActionMenu(null);
                        }}
                          className="w-full text-left px-4 py-2 text-sm text-gray-600 hover:bg-gray-50 flex items-center gap-2">
                          <ArchiveBoxIcon className="h-4 w-4" /> Archive
                        </button>
                      )}
                      <div className="border-t border-gray-100 my-1"></div>
                      <button onClick={() => {
                        setConfirmAction({ clientId: client.id, clientName: client.business_name, action: 'delete' });
                        setActionMenu(null);
                      }}
                        className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 flex items-center gap-2">
                        <TrashIcon className="h-4 w-4" /> Delete Permanently
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        ))}
        {filtered.length === 0 && (
          <div className="text-center py-12 text-gray-400">
            {search || statusFilter !== 'all' ? 'No clients match your filters.' : 'No clients on the trail yet.'}
          </div>
        )}
      </div>

      {/* Add Client Modal */}
      {showAddModal && (
        <AddClientModal
          onClose={() => setShowAddModal(false)}
          onCreated={(c) => setClients(prev => [c, ...prev])}
        />
      )}

      {/* Confirm Action Modal */}
      {confirmAction && (
        <ConfirmModal
          title={
            confirmAction.action === 'delete' ? 'Delete Client Permanently?' :
            confirmAction.action === 'archive' ? 'Archive Client?' :
            confirmAction.action === 'suspend' ? 'Pause Client?' :
            'Reactivate Client?'
          }
          message={
            confirmAction.action === 'delete'
              ? `This will permanently remove ${confirmAction.clientName} and all their content, messages, and phase data. This cannot be undone.`
              : confirmAction.action === 'archive'
              ? `${confirmAction.clientName} will be archived. Their data is preserved but they won't appear in active lists.`
              : confirmAction.action === 'suspend'
              ? `${confirmAction.clientName} will be paused. They won't be able to access the app until reactivated.`
              : `${confirmAction.clientName} will be reactivated and able to access the app again.`
          }
          confirmLabel={
            confirmAction.action === 'delete' ? 'Delete Forever' :
            confirmAction.action === 'archive' ? 'Archive' :
            confirmAction.action === 'suspend' ? 'Pause' : 'Reactivate'
          }
          confirmClass={
            confirmAction.action === 'delete' ? 'bg-red-600 hover:bg-red-700' :
            confirmAction.action === 'archive' ? 'bg-gray-600 hover:bg-gray-700' :
            confirmAction.action === 'suspend' ? 'bg-amber-500 hover:bg-amber-600' :
            'bg-green-600 hover:bg-green-700'
          }
          onConfirm={handleAction}
          onClose={() => setConfirmAction(null)}
        />
      )}
    </div>
  );
}
